import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { UploadCloud, X, Film } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';

interface VideoUploaderProps {
  videoFile: File | null;
  setVideoFile: (file: File | null) => void;
  currentVideoUrl?: string | null;
  onRemoveCurrentVideo?: () => void;
  maxSizeMB?: number;
  label?: string;
  isUploading?: boolean;
  uploadProgress?: number;
}

const VideoUploader: React.FC<VideoUploaderProps> = ({
  videoFile,
  setVideoFile,
  currentVideoUrl,
  onRemoveCurrentVideo,
  maxSizeMB = 20,
  label = "Ngarko Video për Shpalljen (Opsionale, Max 1)",
  isUploading = false,
  uploadProgress = 0,
}) => {
  const { toast } = useToast();
  const [preview, setPreview] = useState<string | null>(null);

  const onDrop = useCallback(async (acceptedFiles: File[], rejectedFiles: any[]) => {
    if (rejectedFiles && rejectedFiles.length > 0) {
      rejectedFiles.forEach((rejectedFile: any) => {
        rejectedFile.errors.forEach((error: any) => {
          if (error.code === 'file-too-large') {
            toast({
              title: "Skedari shumë i madh",
              description: `Videoja duhet të jetë më e vogël se ${maxSizeMB}MB.`,
              variant: "destructive",
            });
          } else if (error.code === 'file-invalid-type') {
            toast({
              title: "Lloji i skedarit i pavlefshëm",
              description: "Ju lutemi ngarkoni një skedar video të vlefshëm (p.sh. MP4, MOV).",
              variant: "destructive",
            });
          } else {
            toast({
              title: "Gabim në ngarkim",
              description: error.message,
              variant: "destructive",
            });
          }
        });
      });
      return;
    }

    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      
      // Clear previous state
      setVideoFile(null);
      if (preview) {
        URL.revokeObjectURL(preview);
        setPreview(null);
      }

      // Accept the file directly without compression
      setVideoFile(file);
      setPreview(URL.createObjectURL(file));
      
      toast({
        title: "Video u shtua",
        description: "Videoja u shtua me sukses.",
        variant: "default",
      });
    }
  }, [setVideoFile, maxSizeMB, toast, preview]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/mp4': ['.mp4', '.m4v'],
      'video/quicktime': ['.mov'],
      'video/webm': ['.webm'],
      'video/x-msvideo': ['.avi'],
      'video/x-matroska': ['.mkv'],
      'video/mpeg': ['.mpeg', '.mpg'],
    },
    maxSize: maxSizeMB * 1024 * 1024,
    multiple: false,
  });

  const handleRemoveFile = () => {
    setVideoFile(null);
    if (preview) {
      URL.revokeObjectURL(preview);
      setPreview(null);
    }
  };
  
  const handleRemoveCurrentVideo = () => {
    if (onRemoveCurrentVideo) {
      onRemoveCurrentVideo();
    }
    setPreview(null);
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      
      {currentVideoUrl && !videoFile && (
        <div className="mt-2 p-2 border rounded-md">
          <p className="text-sm font-medium mb-1">Videoja aktuale:</p>
          <video src={currentVideoUrl} controls className="w-full max-h-60 rounded" />
          {onRemoveCurrentVideo && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleRemoveCurrentVideo}
              className="mt-2"
              disabled={isUploading}
            >
              Hiq videon aktuale
            </Button>
          )}
        </div>
      )}

      {(!currentVideoUrl || (currentVideoUrl && !videoFile)) && !videoFile && (
        <div
          {...getRootProps()}
          className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md cursor-pointer
            ${isDragActive ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'}`}
        >
          <input {...getInputProps()} disabled={isUploading} />
          <div className="space-y-1 text-center">
            <UploadCloud className="mx-auto h-12 w-12 text-gray-400" />
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isDragActive ? "Lëshoni skedarin këtu..." : "Tërhiqeni videon këtu ose klikoni për të zgjedhur"}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">MP4, MOV, WEBM, AVI, MKV deri në {maxSizeMB}MB</p>
          </div>
        </div>
      )}

      {videoFile && (
        <div className="mt-2 p-2 border border-gray-300 dark:border-gray-600 rounded-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Film className="h-6 w-6 text-gray-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-xs">
                {videoFile.name}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                ({(videoFile.size / 1024 / 1024).toFixed(2)} MB)
              </span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemoveFile}
              disabled={isUploading}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {preview && !isUploading && (
            <video src={preview} controls className="w-full max-h-60 rounded mt-2" />
          )}
          {isUploading && (
            <div className="mt-2">
              <Progress value={uploadProgress} className="w-full" indicatorClassName="bg-brand-500" />
              <p className="text-xs text-center mt-1 text-gray-500 dark:text-gray-400">Videoja po ngarkohet...</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoUploader;
